import { useCallback, useEffect, useState } from 'react';
import { useApi } from '../useApi';
import { MARKETING_CONSENT_LABEL, MARKETING_CONSENT_SCOPE } from '../marketingConsentCopy.js';

// Opting out has to be exactly as easy as opting in.
//
// That is not a nicety — a product where consent is one click and withdrawal
// is an email to support does not have consent, it has a mailing list. So this
// lives on a screen the customer can already reach, and takes effect the
// moment it is toggled.

interface Consent {
  consented: boolean;
  consentedAt: string | null;
}

export function MarketingPreference() {
  const api = useApi();
  const [consent, setConsent] = useState<Consent | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!api) return;
    try {
      setConsent(await api.get<Consent>('/me/marketing-consent'));
    } catch {
      // Silent: a preference that failed to load should not put an error
      // banner over the billing page the customer actually came here for.
      setConsent(null);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  async function toggle(next: boolean) {
    if (!api) return;
    setSaving(true);
    setError(null);
    // Optimistic, then corrected by the server's answer — the checkbox should
    // not lag behind the click.
    setConsent({ consented: next, consentedAt: null });
    try {
      setConsent(await api.put<Consent>('/me/marketing-consent', { consented: next }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save that.');
      await load();
    } finally {
      setSaving(false);
    }
  }

  if (!consent) return null;

  return (
    <div className="card">
      <h2>Email preferences</h2>
      {error && <div className="notice error">{error}</div>}
      <label className="checkbox-row">
        <input
          type="checkbox"
          checked={consent.consented}
          disabled={saving}
          onChange={(e) => void toggle(e.target.checked)}
        />
        <span>
          {MARKETING_CONSENT_LABEL}
          <br />
          <small className="muted">{MARKETING_CONSENT_SCOPE}</small>
        </span>
      </label>
    </div>
  );
}
